import { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { EmiCalendarColumnDefCallBack } from 'src/pages/CaseDetails/constants/EmiSchedule.component';
import {
  createKey,
  isForeclosed,
  isOverdue,
  isPaid,
  isPartiallyPaid
} from 'src/utils/CaseDetail.utils';
import { useParams } from 'react-router-dom';
import GridContainer from '@navi/web-ui/lib/layouts/Grid/GridContainer/GridContainer';
import GridRow from '@navi/web-ui/lib/layouts/Grid/GridRow/GridRow';
import GridColumn from '@navi/web-ui/lib/layouts/Grid/GridColumn/GridColumn';
import Chip from '@navi/web-ui/lib/primitives/Chip/Chip';
import { ChipObjMap } from 'src/pages/CaseDetails/constants/EmiSchedule.component';
import { CustomerDetailApiResponse, Due } from '../../interfaces/CaseDetail.type';
import SuspenseLoader from 'src/components/SkeletonLoader/SuspenseLoader';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import CalculateOverDue from './CalculateOverdue';
import { QueryParams } from 'src/components/interfaces';
import styles from '../EmiSchedule/index.module.scss';
import { fetchPendingFeeComponents } from '../../actions/EmiScheduleActions';
import WaiveFeeDrawer from './WaiveFeeDrawer';
import EmiCalendarActionCell from './EmiCalendarActionCell';
import { EmiFlowType, PendingFeeComponent } from '../../interfaces/EmiSchedule.type';
import TableLoader from 'src/components/TableLoader/TableLoader';
import PaymentHistoryOverlay from './PaymentHistoryOverlay';
import EmiBreakupOverlay from './EmiBreakupOverlay';
import useOverlay from 'src/hooks/useOverlay';
import { EMI_CALENDAR_ACTIONS } from '../../constants/EmiSchedule.constant';
import FeeWaiverHistory from './FeeWaiverHistory';
import EmiBreakupRow from './EmiBreakupRow';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';
import WaiveFeeDrawerV2 from './WaiveFeeDrawerV2';
import FeeWaiverHistoryV2 from './FeeWaiverHistoryV2';
import NaviTable from '@cp/src/components/NaviTable/NaviTable';

const assignIndex = (allDues: Due[] = []) => {
  if (!allDues.length) return;
  let clickedRowIndex = -1;
  let currentRowIndex;
  return allDues?.map((due, index) => {
    if (due.toggleState) {
      clickedRowIndex = index;
    }
    if (clickedRowIndex != -1 && clickedRowIndex < index) {
      currentRowIndex = index;
    } else {
      currentRowIndex = index + 1;
    }
    return { ...due, index: currentRowIndex };
  });
};

const EmiCalendar = (props: {
  queryParams: QueryParams;
  updateQueryParamsAndNavigate: (params: { selectedChip: string }) => void;
}) => {
  const { queryParams, updateQueryParamsAndNavigate } = props;
  const dispatch = useDispatch();
  const { loanId = '', customerId = '' } = useParams();
  const { isOpen: isEmiBreakupOverlayOpen, toggle: toggleEmiBreakupOverlay } = useOverlay();
  const { isOpen: isRepaymentDueOpen, toggle: toggleRepaymentDueOverlay } = useOverlay();
  const detailsApiPayload = useSelector(
    (state: RootState) => state?.caseDetail?.pageData?.[createKey(loanId, customerId)]?.details
  );
  const featureFlags = useSelector((state: RootState) => state?.common?.featureFlags);
  const editAccessFlag = detailsApiPayload?.data?.editAccessFlag || false;
  const { feeWaiverFeatureFlag } = featureFlags || {};
  const pendingFeeComponents = useSelector(
    (state: RootState) =>
      state.caseDetail.pageData?.[createKey(loanId, customerId)]?.pendingFeeComponents
  );
  const { data, loading } = detailsApiPayload || {};
  const { allDues = [] } = (data as CustomerDetailApiResponse) || {};
  const [localDues, setLocalDues] = useState(assignIndex(allDues));
  const [accordionDetails, setAccordionDetails] = useState<any>(null);
  const [isWaiveFeeDrawerOpen, setIsWaiveFeeDrawerOpen] = useState(false);
  const [isWaiveFeeDrawerOpenV2, setIsWaiveFeeDrawerOpenV2] = useState(false);
  const [feeComponentAdjustmentsForDue, setFeeComponentAdjustmentsForDue] = useState<
    PendingFeeComponent[]
  >([]);
  const [paymentHistoryReferenceId, setPaymentHistoryReferenceId] = useState<string>('');
  const [emiBreakupReferenceId, setEmiBreakupReferenceId] = useState<string>('');
  const [dueDetails, setDueDetails] = useState<Due | undefined>();

  const currentChip = queryParams?.selectedChip || ChipObjMap.ALL_EMI.value;

  const emiCalendarColumnDef = useMemo(() => {
    const emiCalendarColumnDef = EmiCalendarColumnDefCallBack();
    emiCalendarColumnDef[emiCalendarColumnDef.length - 1].cell = info => {
      return (
        <EmiCalendarActionCell
          isExpanded={info.row.getIsExpanded()}
          pendingFeeComponents={pendingFeeComponents}
          toggleWaiveFeeDrawer={toggleWaiveFeeDrawer}
          setFeeComponentAdjustmentsForDue={setFeeComponentAdjustmentsForDue}
          rowData={info.row.original}
          handleReferenceId={handleReferenceId}
          setDueDetails={setDueDetails}
          setFlowType={setFlowType}
          toggleWaiveFeeDrawerV2={toggleWaiveFeeDrawerV2}
          editAccessFlag={editAccessFlag}
        />
      );
    };
    return emiCalendarColumnDef;
  }, [localDues, pendingFeeComponents]);

  useEffect(() => {
    if (!pendingFeeComponents) {
      dispatch(fetchPendingFeeComponents(loanId, customerId));
    }
  }, [pendingFeeComponents]);

  useMemo(() => {
    if (allDues) {
      ChipObjMap.SCHEDULED.count =
        ChipObjMap.OVERDUE.count =
        ChipObjMap.PAID.count =
        ChipObjMap.PARTIALLY_PAID.count =
        ChipObjMap.FORECLOSED.count =
          0;
      ChipObjMap.ALL_EMI.count = allDues.length;
      for (const due of allDues) {
        if (isOverdue(due as Due)) {
          ChipObjMap.OVERDUE.count++;
          continue;
        }
        if (isPaid(due as Due)) {
          ChipObjMap.PAID.count++;
          continue;
        }
        if (isForeclosed(due as Due)) {
          ChipObjMap.FORECLOSED.count++;
          continue;
        }
        if (isPartiallyPaid(due as Due)) {
          ChipObjMap.PARTIALLY_PAID.count++;
          continue;
        }
        ChipObjMap.SCHEDULED.count++;
      }
    }
  }, [allDues]);

  useEffect(() => {
    if (allDues?.length) setLocalDues(assignIndex(allDues));
  }, [allDues]);

  const handleReferenceId = (refId: string, type: string) => {
    if (type === EMI_CALENDAR_ACTIONS.EMI_BREAKUP.value) {
      setEmiBreakupReferenceId(refId);
      toggleEmiBreakupOverlay();
    }
    if (type === EMI_CALENDAR_ACTIONS.REPAYMENT_DUE.value) {
      setPaymentHistoryReferenceId(refId);
      toggleRepaymentDueOverlay();
      addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_EMISchedule_PaymentHistory);
    }
  };

  const handleChipClick = useCallback(
    (chipValue: string) => {
      return () => {
        if (chipValue === currentChip) return;
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_EMISchedule_QuickFilter, {
          selectedFilter: chipValue
        });
        updateQueryParamsAndNavigate({
          selectedChip: chipValue
        });
      };
    },
    [queryParams]
  );

  const doesExternalFilterPass = useCallback(
    (node: { data: Due }) => {
      if (node.data) {
        switch (currentChip) {
          case ChipObjMap.PAID.value:
            return isPaid(node.data);
          case ChipObjMap.OVERDUE.value:
            return isOverdue(node.data as Due);
          case ChipObjMap.FORECLOSED.value:
            return isForeclosed(node.data as Due);
          case ChipObjMap.PARTIALLY_PAID.value:
            return isPartiallyPaid(node.data as Due);
          case ChipObjMap.SCHEDULED.value:
            return (
              !isPaid(node.data as Due) &&
              !isOverdue(node.data as Due) &&
              !isPartiallyPaid(node.data as Due) &&
              !isForeclosed(node.data as Due)
            );
          default:
            return true;
        }
      }
    },
    [currentChip]
  );

  useEffect(() => {
    setLocalDues(assignIndex(allDues?.filter(due => doesExternalFilterPass({ data: due }))));
  }, [currentChip, allDues]);

  const renderChipCount = (chip: (typeof ChipObjMap)[keyof typeof ChipObjMap]) => {
    return (
      <>
        <div className={styles.chipCount}>{chip.count}</div>
      </>
    );
  };

  const toggleWaiveFeeDrawerV2 = (open: boolean) => {
    setIsWaiveFeeDrawerOpenV2(open);
  };

  const toggleWaiveFeeDrawer = (open: boolean) => {
    setIsWaiveFeeDrawerOpen(open);
  };

  const renderChips = useMemo(() => {
    return Object.values(ChipObjMap).map((chip: (typeof ChipObjMap)[keyof typeof ChipObjMap]) => {
      return chip.count ? (
        <Chip
          key={chip.value}
          wrapperClasses={cx([
            styles['emiCalendar__chip'],
            {
              [styles['emiCalendar__chip__chipActiveColor']]: currentChip === chip.value
            },
            {
              [styles['emiCalendar__chip__disabled']]: chip.count === 0
            }
          ])}
          label={chip.label}
          RightAdornment={renderChipCount(chip)}
          labelClasses={cx(styles['emiCalendar__chip__label'], {
            [styles['activeLable']]: currentChip !== chip.value
          })}
          onChipClick={handleChipClick(chip.value)}
        />
      ) : null;
    });
  }, [currentChip, allDues]);

  const renderLabel = useMemo(
    () => (
      <GridRow>
        <GridColumn md={12} className="emiCalendar__label">
          <Typography variant="h3">EMI calendar</Typography>
        </GridColumn>
      </GridRow>
    ),
    []
  );
  const [flowType, setFlowType] = useState<Record<string, EmiFlowType>>({});

  useEffect(() => {
    if (accordionDetails) {
      setAccordionDetails(null);
    }
  }, [localDues]);

  const closeWaiveFeeDrawerV2 = () => {
    toggleWaiveFeeDrawerV2(false);
  };

  return (
    <GridContainer className={styles['emiCalendar']}>
      {renderLabel}
      <GridRow className="no-gutters">
        <GridColumn md={12}>
          <SuspenseLoader loading={!!loading} fallBack={<TableLoader numberOfLines={11} />}>
            <GridRow>
              <GridColumn md={8} className={styles['emiCalendar__chips']}>
                {renderChips}
              </GridColumn>
              <GridColumn
                sm={2}
                className={cx(styles.emiCalendar__actions, styles.feeWaiverHistory)}
              >
                {feeWaiverFeatureFlag ? <FeeWaiverHistoryV2 /> : <FeeWaiverHistory />}
              </GridColumn>
              <GridColumn sm={2} className={styles['emiCalendar__actions']}>
                <CalculateOverDue />
              </GridColumn>
            </GridRow>
            <GridRow className="emiCalendar">
              <NaviTable
                data={localDues || []}
                columns={emiCalendarColumnDef}
                expandedRowComponent={row => {
                  return <EmiBreakupRow rowData={row} />;
                }}
                getRowCanExpand={() => true}
                loading={loading}
                bodyRowStyles={() => 'rr-block'}
              />
            </GridRow>
          </SuspenseLoader>
        </GridColumn>
      </GridRow>
      <WaiveFeeDrawer
        phoneNumber={data?.phoneNumber}
        phoneNumberCustomId={data?.phoneNumberCustomId || ''}
        pendingFeeComponents={feeComponentAdjustmentsForDue}
        dueDetails={dueDetails}
        isOpen={isWaiveFeeDrawerOpen}
        toggleDrawer={toggleWaiveFeeDrawer}
        flowType={flowType}
      />
      {dueDetails && (
        <WaiveFeeDrawerV2
          isOpen={isWaiveFeeDrawerOpenV2}
          closeDrawer={closeWaiveFeeDrawerV2}
          emiItem={dueDetails}
        />
      )}
      <EmiBreakupOverlay
        refId={emiBreakupReferenceId}
        isOpen={isEmiBreakupOverlayOpen}
        toggleOverlay={toggleEmiBreakupOverlay}
      />
      <PaymentHistoryOverlay
        refId={paymentHistoryReferenceId}
        isOpen={isRepaymentDueOpen}
        toggleOverlay={toggleRepaymentDueOverlay}
      />
    </GridContainer>
  );
};

export default EmiCalendar;
